import { endOfDay, isToday, startOfDay } from 'date-fns';

export type Intervalo = { inicio: Date; fim: Date };

const chavesDiaSemana = ['dom', 'seg', 'ter', 'qua', 'qui', 'sex', 'sab'] as const;

const horaParaData = (dia: Date, horaHHmmss: string): Date => {
  const [h, m, s] = horaHHmmss.split(':').map(Number);
  const d = new Date(dia);
  d.setHours(h ?? 0, m ?? 0, s ?? 0, 0);
  return d;
};

const mesclarIntervalos = (intervalos: Intervalo[]): Intervalo[] => {
  const ordenados = [...intervalos].sort((a, b) => a.inicio.getTime() - b.inicio.getTime());
  const resultado: Intervalo[] = [];
  for (const it of ordenados) {
    const ultimo = resultado[resultado.length - 1];
    if (ultimo && it.inicio.getTime() <= ultimo.fim.getTime()) {
      if (it.fim.getTime() > ultimo.fim.getTime()) ultimo.fim = it.fim;
    } else {
      resultado.push({ ...it });
    }
  }
  return resultado;
};

// Expediente do dia: união dos horários (`agenda_horario`) configurados nas
// agendas do usuário para o dia da semana de `dia`. Sem nenhuma agenda
// cadastrada, assume 24h (padrão do sistema); havendo agenda(s) mas nenhum
// horário para esse dia da semana específico, não há expediente algum.
export const expedienteDoDia = (
  agendas: any[],
  dia: Date,
): { janelas: Intervalo[]; duracaoMinimaMin: number } => {
  if (!agendas || agendas.length === 0) {
    return {
      janelas: [{ inicio: startOfDay(dia), fim: endOfDay(dia) }],
      duracaoMinimaMin: 30,
    };
  }

  const chave = chavesDiaSemana[dia.getDay()];
  const horarios = agendas.flatMap(a => a.horarios ?? []).filter((h: any) => h?.[chave]);

  if (horarios.length === 0) {
    return { janelas: [], duracaoMinimaMin: 0 };
  }

  const janelas = mesclarIntervalos(
    horarios.map((h: any) => ({
      inicio: horaParaData(dia, h.horario_inicio),
      fim: horaParaData(dia, h.horario_fim),
    })),
  );

  const duracaoMinimaMin = Math.min(...horarios.map((h: any) => h.duracao_minutos || 30));

  // Não exibe janelas já encerradas hoje, nem propõe criar atividade no passado.
  // O corte é alinhado ao grid de slots da janela (múltiplos de duracaoMinimaMin
  // a partir do início original), não à hora exata: com expediente 8h–12h e
  // slots de 30min, às 10:33 o próximo horário disponível é 11:00, não 10:33.
  if (isToday(dia)) {
    const agora = new Date();
    const passoMs = duracaoMinimaMin * 60 * 1000;
    for (const janela of janelas) {
      if (janela.inicio < agora) {
        const origemMs = janela.inicio.getTime();
        const passosDecorridos = Math.ceil((agora.getTime() - origemMs) / passoMs);
        janela.inicio = new Date(origemMs + passosDecorridos * passoMs);
      }
    }
  }

  return { janelas: janelas.filter(j => j.inicio < j.fim), duracaoMinimaMin };
};

// Slots livres = expediente menos os intervalos ocupados, descartando buracos
// menores que a menor duração de slot configurada (ou 30min, no padrão de 24h).
export const slotsLivres = (
  janelas: Intervalo[],
  ocupados: Intervalo[],
  duracaoMinimaMin: number,
): Intervalo[] => {
  const ocupadosMesclados = mesclarIntervalos(ocupados);
  const resultado: Intervalo[] = [];

  for (const janela of janelas) {
    let cursor = janela.inicio.getTime();
    const fimJanela = janela.fim.getTime();

    for (const ocupado of ocupadosMesclados) {
      const inicioOcupado = Math.max(ocupado.inicio.getTime(), cursor);
      const fimOcupado = Math.min(ocupado.fim.getTime(), fimJanela);
      if (fimOcupado <= cursor) continue;
      if (inicioOcupado >= fimJanela) break;

      if (inicioOcupado > cursor) {
        resultado.push({ inicio: new Date(cursor), fim: new Date(inicioOcupado) });
      }
      cursor = Math.max(cursor, fimOcupado);
    }

    if (cursor < fimJanela) {
      resultado.push({ inicio: new Date(cursor), fim: new Date(fimJanela) });
    }
  }

  const minMs = duracaoMinimaMin * 60 * 1000;
  return resultado.filter(s => s.fim.getTime() - s.inicio.getTime() >= minMs);
};

export const formatDuracao = (ms: number): string => {
  const minutos = Math.round(ms / 60000);
  const h = Math.floor(minutos / 60);
  const m = minutos % 60;
  if (h && m) return `${h}h${m}min`;
  if (h) return `${h}h`;
  return `${m}min`;
};
